import { Component, OnInit, inject } from '@angular/core';
import { MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogContent } from '@angular/material/dialog';
import { select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';

import { SearchDisplayType } from '@sales/modules/mobilephone/models/mobilephone-search-enums';
import { ApiAccessoryFilterActions, ApiAccessorySearchAction } from '../../../states/accessory-search.actions';
import { AccessorySearchSelectors } from '../../../states/accessory-search.selector';
import { AccessoryProductAddAction } from '@sales/states/accessory-product/accessory-product.actions';
import { HardwareProduct } from '@shared/models/hardware-product.model';
import { Equipment } from '@sales/models/order-capture.model';
import { Price } from '@shared/models/price.model';
import { AccessoryItem, ProductTypes, SubscriptionForHardwareOffering } from '@sales/models/accessory.model';
import { IconNames } from '@shared/enums/icon-names';
import { ApiGetStockAction } from '@sales/states/warehouse/warehouse.actions';
import { WarehouseStateSelectors } from '@sales/states/warehouse/warehouse.selectors';
import { Theme } from '@app/support/models/theme.model';
import { ThemeService } from '@app/core/services/theme.service';
import { HardwareCategory } from '@app/shared/models/product.model';
import { PaymentMethod, PhoneNumberOption } from '../../../models/accessory-options.model';
import { PaymentType } from '@app/shared/enums/payment-type';
import { AccessoryDetailsComponent } from './accessory-details/accessory-details.component';
import { HardwareSearchResultComponent } from '@app/shared/components/hardware-search-result/hardware-search-result.component';
import { AccessoryFiltersComponent } from './accessory-filters/accessory-filters.component';
import { IceSearchFieldComponent } from '@app/shared/components/molecules/ice-search-field/ice-search-field.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { IceIconButtonComponent } from '@app/shared/components/atoms/ice-icon-button/ice-icon-button.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

type AddAccessoryItem = {
  subscription: SubscriptionForHardwareOffering;
  phoneNumberOption: PhoneNumberOption;
  paymentType: PaymentMethod;
  voiceProductId?: string;
  subscriptionIdToUpgrade?: string;
  accessoryItem: AccessoryItem;
};

@Component({
  selector: 'app-accessory-search',
  templateUrl: './accessory-search.component.html',
  styleUrls: ['./accessory-search.component.scss'],
  imports: [
    MatDialogTitle,
    MatIcon,
    NgClass,
    IceIconButtonComponent,
    MatDialogClose,
    CdkScrollable,
    MatDialogContent,
    IceSearchFieldComponent,
    AccessoryFiltersComponent,
    HardwareSearchResultComponent,
    AccessoryDetailsComponent,
    AsyncPipe,
  ],
})
export class AccessorySearchComponent implements OnInit {
  dialogRef = inject<MatDialogRef<AccessorySearchComponent>>(MatDialogRef);
  private store = inject(Store);
  private themeService = inject(ThemeService);

  IconNames = IconNames;

  searchResults = select(AccessorySearchSelectors.allSearchResults);
  isLoadingAccessorySearchResults = select(AccessorySearchSelectors.isLoadingAccessorySearchResults);
  accessoryFilters = select(AccessorySearchSelectors.allFilters);
  isLoadingAccessoryFilters = select(AccessorySearchSelectors.isLoadingFilters);
  warehouseState = select(WarehouseStateSelectors.getState);

  filteredAccessories: AccessoryItem[];
  selectedAccessoryItem: AccessoryItem = null;
  displayType = SearchDisplayType.Grid;
  placeholderText: string;
  searchValue = '';
  mainCategory: string = '';
  manufacturers = [];
  subCategories = [];
  displayEligibleForAccessoryBinding = false;
  tabindex = 0;
  selectedTheme$: Observable<Theme>;

  ngOnInit() {
    this.selectedTheme$ = this.themeService.selectedTheme;
    this.store.dispatch([new ApiAccessoryFilterActions.FetchAll(), new ApiAccessorySearchAction()]);
  }

  onSearchChange(searchTerm: string) {
    this.searchValue = searchTerm;

    this.executeCommonFilterDispatch();
  }

  onManufacturerChange(selectedManufacturers: string[]) {
    this.manufacturers = selectedManufacturers;

    this.executeCommonFilterDispatch();
  }

  onMainCategoryChange(selectedMainCategory: string) {
    this.mainCategory = selectedMainCategory;

    this.executeCommonFilterDispatch();
  }

  onSubCategoryChange(selectedSubCategories: string[]) {
    this.subCategories = selectedSubCategories;

    this.executeCommonFilterDispatch();
  }

  onlyDisplayEligibleForAccessoryBinding(onlyDisplayEligible: boolean) {
    this.displayEligibleForAccessoryBinding = onlyDisplayEligible;

    this.executeCommonFilterDispatch();
  }

  private executeCommonFilterDispatch() {
    const allAccessories = this.store.selectSnapshot(AccessorySearchSelectors.allSearchResults);

    const hasSearchText = this.searchValue.length > 0;
    const hasSelectedManufacturers = this.manufacturers.length > 0;
    const hasSelectedSubCategories = this.subCategories.length > 0;
    const hasMainCategory = !!this.mainCategory;

    const searchTextFilter = x => (hasSearchText ? x.name.toLowerCase().includes(this.searchValue.toLowerCase()) : true);
    const manufacturerFilter = x => (hasSelectedManufacturers ? this.manufacturers.includes(x.manufacturer) : true);
    const subCategoryFilter = x => (hasSelectedSubCategories ? this.subCategories.includes(x.subCategory) : true);
    const mainCategoryFilter = x => (hasMainCategory ? this.mainCategory === x.mainCategory : true);
    const accessoryBindingFilter = x =>
      this.displayEligibleForAccessoryBinding ? x.hardwareOfferingWithSubscriptionPrice != null : true;

    if (
      !hasSearchText &&
      !hasSelectedManufacturers &&
      !hasSelectedSubCategories &&
      !hasMainCategory &&
      !this.displayEligibleForAccessoryBinding
    ) {
      this.filteredAccessories = [];
    } else {
      const filtered = allAccessories.filter(
        a =>
          searchTextFilter(a) &&
          manufacturerFilter(a) &&
          subCategoryFilter(a) &&
          mainCategoryFilter(a) &&
          accessoryBindingFilter(a)
      );
      this.filteredAccessories = filtered;
    }
  }

  get accessories(): AccessoryItem[] {
    return !!this.filteredAccessories?.length ? this.filteredAccessories : this.searchResults();
  }

  onDisplayTypeChange(displayType: SearchDisplayType) {
    this.displayType = displayType;
  }

  selectAccessoryItem(accessoryItem: AccessoryItem) {
    this.selectedAccessoryItem = accessoryItem;
    this.store.dispatch(new ApiGetStockAction([accessoryItem.offeringId]));
  }

  onAddAccessoryItem(data: AddAccessoryItem) {
    const { subscription, accessoryItem, paymentType, subscriptionIdToUpgrade } = data;
    const price = new Price(
      this.selectedAccessoryItem.priceInformation.price.includingVAT,
      this.selectedAccessoryItem.priceInformation.price.excludingVAT,
      this.selectedAccessoryItem.priceInformation.price.vatPercentage
    );

    const equipment = new Equipment();
    equipment.offeringId = this.selectedAccessoryItem.offeringId;
    equipment.name = this.selectedAccessoryItem.name;
    equipment.gtin = this.selectedAccessoryItem.gtin;
    equipment.price = price;
    equipment.image = this.selectedAccessoryItem.image ? this.selectedAccessoryItem.image.previewUri : '';
    equipment.isManualRegistration = false;

    const hardware = new HardwareProduct();
    hardware.equipment = equipment;
    hardware.hardwareCategory = this.getHardwareCategory(this.selectedAccessoryItem.productType);
    hardware.paymentType =
      hardware.hardwareCategory === HardwareCategory.MBBAccessory ? PaymentType.PayWithNextInvoice : PaymentType.Upfront;

    if (paymentType === PaymentMethod.PayWithSubscription) {
      hardware.paymentType = PaymentType.Upfront;
      hardware.selectedSubscription = subscription;
      hardware.subscriptionIdToUpgrade = subscriptionIdToUpgrade;
      hardware.accessoryVariant = accessoryItem;
      hardware.subscriptionOfferingId = subscription.subscriptionOfferingId;
    }
    this.store.dispatch(new AccessoryProductAddAction(hardware));

    if (paymentType === PaymentMethod.PayWithSubscription) {
      const navigateToSubscriptionPage = true;
      this.dialogRef.close(navigateToSubscriptionPage);
    } else {
      const navigateToSubscriptionPage = false;
      this.dialogRef.close(navigateToSubscriptionPage);
    }
  }

  private getHardwareCategory(productType: ProductTypes): HardwareCategory {
    return productType === ProductTypes.MbbEquipment ? HardwareCategory.MBBAccessory : HardwareCategory.Accessory;
  }
}
